import { showToast } from "@/lib/toast";
import { useCallback, useState } from "react";

const UPLOADS_URL = process.env.NEXT_PUBLIC_UPLOADS_URL || "https://uploads.b3.fun";
const LOCAL_API_KEY = process.env.NEXT_PUBLIC_B3_UPLOADS_LOCAL_API_KEY;

interface UploadResponse {
  success: boolean;
  data?: { url: string; filename: string; size: number; contentType: string };
  error?: string;
}

interface UseFileUploadOptions {
  maxSizeInMB?: number;
  acceptedTypes?: string[];
  onSuccess?: (url: string) => void;
  onError?: (error: Error) => void;
}

interface UseFileUploadReturn {
  uploadFile: (file: File) => Promise<string | null>;
  uploadBlob: (blob: Blob, fileName: string) => Promise<string | null>;
  isUploading: boolean;
  error: Error | null;
}

export function useFileUpload(options: UseFileUploadOptions = {}): UseFileUploadReturn {
  const { maxSizeInMB = 5, acceptedTypes = [], onSuccess, onError } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const validateFile = useCallback(
    (file: File): boolean => {
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        const error = new Error(`File size must be less than ${maxSizeInMB}MB`);
        setError(error);
        showToast.error(error.message);
        return false;
      }

      if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
        const error = new Error(`File type ${file.type} is not accepted. Accepted types: ${acceptedTypes.join(", ")}`);
        setError(error);
        showToast.error(error.message);
        return false;
      }

      return true;
    },
    [maxSizeInMB, acceptedTypes],
  );

  const uploadFile = useCallback(
    async (file: File): Promise<string | null> => {
      if (!validateFile(file)) {
        return null;
      }

      setIsUploading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const headers: Record<string, string> = {};
        if (LOCAL_API_KEY && typeof window !== "undefined" && window.location.hostname === "localhost") {
          headers["Authorization"] = `Bearer ${LOCAL_API_KEY}`;
        }

        const res = await fetch(`${UPLOADS_URL}/upload/image`, {
          method: "POST",
          body: formData,
          headers,
        });

        if (!res.ok) {
          const body: UploadResponse = await res.json().catch(() => ({ success: false, error: "Upload failed" }));
          throw new Error(body.error || `Upload failed (${res.status})`);
        }

        const body: UploadResponse = await res.json().catch(() => ({ success: false, error: "Invalid response" }));
        if (!body.success || !body.data) {
          throw new Error(body.error || "Upload failed");
        }

        const url = body.data.url;
        onSuccess?.(url);
        return url;
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to upload file");
        setError(error);
        showToast.error(error.message);
        onError?.(error);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, onSuccess, onError],
  );

  const uploadBlob = useCallback(
    async (blob: Blob, fileName: string): Promise<string | null> => {
      const file = new File([blob], fileName, { type: blob.type });
      return uploadFile(file);
    },
    [uploadFile],
  );

  return {
    uploadFile,
    uploadBlob,
    isUploading,
    error,
  };
}
